/* @flow */
import AffiliationShape, { isAdmin } from 'shapes/Affiliation';
import Button from 'components/Button.jsx';
import Coin from 'components/Coin.jsx';
import CircleSpinner from 'components/CircleSpinner.jsx';
import CurrentUser from 'auth/CurrentUser';
import FlexibleBlock from 'components/FlexibleBlock.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import Image from 'components/Image.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import Organization from 'entities/Organization';
import OrgRoleDropdownMenu from 'groups/components/OrgRoleDropdownMenu.jsx';
import React from 'react';
import AdminResignModal from 'admin/components/AdminResignModal.jsx';
import SettingsRemoveSchoolModal from './SettingsRemoveSchoolModal.jsx';
import Text from 'components/Text.jsx';
import { partition, xor } from 'lodash';
import { roleNameSingular } from 'utils/roleHelpers';
import cx from 'classnames';

import './styles/SettingsSchoolsListItem.scss';

import SCHOOL_IMG_SRC from 'assets/images/school-circle.svg';

const AVATAR_NUMBER = 3;

class SettingsSchoolsListItem extends React.Component {
  static propTypes = {
    affiliation: AffiliationShape,
    school: React.PropTypes.instanceOf(Organization).isRequired,
    groups: React.PropTypes.arrayOf(React.PropTypes.instanceOf(Group)),
    submitting: React.PropTypes.bool,
  };

  static defaultProps = {
    submitting: false,
    groups: [],
  };

  state = {
    open: false,
    hovered: false,
    savingRole: false,
    roles: this.props.affiliation && this.props.affiliation.roles,
  };

  renderHoverContent() {
    const { groups, affiliation, school } = this.props;

    const groupsByOwned = partition(groups, group =>
      group.isOwnedByUser(CurrentUser.getUserId())
    );

    const removeSchoolModal = (
      <SettingsRemoveSchoolModal
        affiliation={affiliation}
        ownedGroups={groupsByOwned[0]}
        subscribedGroups={groupsByOwned[1]}
        school={school}
      />
    );
    return (
      <div className="SettingsSchoolsListItem-actionButtons">
        {affiliation && isAdmin(affiliation) &&
          <ModalTrigger
            className="SettingsSchoolsListItem-removeAdminButton"
            modal={<AdminResignModal affiliation={affiliation} school={school} />}>
            <Button
              color="lava"
              disabled={this.props.submitting}
              level="secondary"
              size="xsmall">
              Remove me as admin
            </Button>
          </ModalTrigger>}
        <ModalTrigger
          className="SettingsSchoolsListItem-leaveButton"
          modal={removeSchoolModal}>
          <Button
            color="lava"
            disabled={this.props.submitting}
            level="secondary"
            size="xsmall">
            Leave
          </Button>
        </ModalTrigger>
      </div>
    );
  }

  renderGroupAvatars() {
    const { groups } = this.props;
    const avatars = groups.slice(0, AVATAR_NUMBER).map(g => {
      return (
        <Image
          className="SettingsSchoolsListItem-group"
          key={g.id}
          src={g.getAvatarUrl()}
        />
      );
    });
    return (
      <div>
        {avatars}
        {
          groups.length > AVATAR_NUMBER &&
            <Coin
              className="SettingsSchoolsListItem-group"
              color="white"
              border="brand"
              size="xlarge">
              +{groups.length - AVATAR_NUMBER}
            </Coin>
        }
      </div>
    );
  }

  renderIcon(school: Organization) {
    const { affiliation } = this.props;
    const userIsAdmin = affiliation && isAdmin(affiliation);
    const image = <Image height={32} width={32} src={SCHOOL_IMG_SRC} />;
    const badge = (
      <Coin
        className="SettingsSchoolsListItem-icon-badge"
        color={userIsAdmin ? 'midnight' : 'shamrock'}
        size="small">
        <Glyph name={userIsAdmin ? 'crown' : 'check'} color="white" solo />
      </Coin>
    );

    if (!school.hasAdmins()) {
      return image;
    }

    return (
      <div>
        {image}
        {badge}
      </div>
    );
  }

  renderByline() {
    if (!this.props.affiliation) {
      return;
    }

    const roleString = this.state.roles.map(e => roleNameSingular[e]).join(', ');

    if (!this.props.affiliation.can_have_role_changed || this.state.roles.length === 0 || this.state.savingRole) {
      return (
        <div>
          <span className="SettingsSchoolsListItem-roleString">{roleString}</span>
          {this.state.savingRole && <CircleSpinner className="SettingsSchoolsListItem-spinner" color="shamrock" size="small" />}
        </div>
      );
    }

    const onClose = (roles) => {
      if (xor(roles, this.state.roles).length > 0) {
        this.setState({ savingRole: true, roles });
      }
      this.setState({ open: false });
    };

    const roleDropdown = (
      <OrgRoleDropdownMenu
        isCurrentUser
        organization={this.props.school}
        roles={this.state.roles}
        potentialRoles={this.props.affiliation.potential_roles}
        userId={CurrentUser.getUserId()}
        onClose={onClose}
        onSave={() => this.setState({ savingRole: false })}
      />
    );

    return (
      <LayerTrigger
        layer={roleDropdown}
        trigger="click"
        placement="bottom"
        alignment="left">
        <span
          className="SettingsSchoolsListItem-editableRole"
          onClick={() => this.setState({ open: !this.state.open })}>
          <span className="SettingsSchoolsListItem-editableRole-text">{roleString}</span>
          <Glyph
            name="chevron-down"
            color="thunder"
            size="xxsmall"
            className={cx({
              'SettingsSchoolsListItem-editableRole-chevron': true,
              'SettingsSchoolsListItem-editableRole-chevronClosed': !this.state.open,
              'SettingsSchoolsListItem-editableRole-chevronOpen': this.state.open,
            })}
          />
        </span>
      </LayerTrigger>
    );
  }

  render() {
    const { school, submitting } = this.props;
    return (
      <div
        className="SettingsSchoolsListItem"
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}>
        <FlexibleBlock flex="left">
          <FlexibleBlock flex="right" className="SettingsSchoolsListItem-school">
            <div className="SettingsSchoolsListItem-icon">
              {this.renderIcon(school)}
            </div>
            <div className="SettingsSchoolsListItem-content">
              <div className="SettingsSchoolsListItem-name">
                <Text size="small" color={submitting ? 'eclipse' : 'pitch'}>
                  {school.getName()}
                </Text>
              </div>
              <div className="SettingsSchoolsListItem-byline">
                <Text size="small" color="thunder">
                  {this.renderByline()}
                </Text>
              </div>
            </div>
          </FlexibleBlock>
          {
            this.state.hovered ?
              this.renderHoverContent() :
              this.renderGroupAvatars()
          }
        </FlexibleBlock>
      </div>
    );
  }
}

export default SettingsSchoolsListItem;



// WEBPACK FOOTER //
// ./src/settings/components/SettingsSchoolsListItem.jsx